import { api } from '@goaltree/backend/convex/_generated/api'
import type { Doc, Id } from '@goaltree/backend/convex/_generated/dataModel'
import { cleanLocation } from '@goaltree/backend/convex/lib/captureDates'
import { HABIT_ICONS } from '@goaltree/backend/convex/lib/habitIcons'
import { type RecursPeriod, resolveSchedule } from '@goaltree/backend/convex/lib/schedule'
import type { OptimisticLocalStore } from 'convex/browser'

import { optimisticHabitCompletion } from '@/lib/habit-optimistic'

// Optimistic node edits across every cached node read (Today, habits, inbox and
// goal lists, goal trees, the task sheet) so each surface reacts before the server.
// In a patch, `undefined` leaves a field alone and `null` clears it.

export type NodePatch = {
  title?: string
  description?: string | null
  tags?: string[]
  completedAt?: number | null
  status?: 'backlog' | 'in-progress' | 'done'
  effort?: 'low' | 'medium' | 'high'
  superUrgent?: boolean | null
  dueDate?: number | null
  dueBefore?: boolean | null
  recurs?: boolean | null
  recursPeriod?: string | null
  recursDays?: number[] | null
  recurrenceDay?: number | null
  recurrenceTime?: string | null
  durationMinutes?: number | null
  allDay?: boolean
  location?: string | null
  category?: string
  icon?: string | null
  streakDays?: number
  mustDoOn?: string | null
  mustDo?: boolean
  putOffDays?: number
}

/** The row as currently cached, in whichever shape the query returns. */
export type NodeLike = { [K in keyof NodePatch]?: NodePatch[K] }

type PatchInput = NodePatch | ((prev: NodeLike) => NodePatch)

type Target = { keys: ReadonlySet<string> | null; nullable: ReadonlySet<string> }

const TODAY: Target = {
  keys: new Set([
    'title',
    'description',
    'tags',
    'completedAt',
    'status',
    'effort',
    'superUrgent',
    'dueDate',
    'dueBefore',
    'recurs',
    'recursPeriod',
    'recursDays',
    'recurrenceDay',
    'recurrenceTime',
    'durationMinutes',
    'allDay',
    'location',
    'category',
    'mustDo',
    'putOffDays',
  ]),
  nullable: new Set([
    'description',
    'completedAt',
    'dueDate',
    'recursPeriod',
    'recursDays',
    'recurrenceDay',
    'recurrenceTime',
    'durationMinutes',
  ]),
}

const HABITS: Target = {
  keys: new Set([
    'title',
    'description',
    'tags',
    'completedAt',
    'status',
    'effort',
    'superUrgent',
    'dueDate',
    'recurs',
    'recursPeriod',
    'recursDays',
    'recurrenceDay',
    'recurrenceTime',
    'durationMinutes',
    'allDay',
    'category',
    'icon',
    'streakDays',
  ]),
  nullable: new Set([
    'description',
    'completedAt',
    'dueDate',
    'recursDays',
    'recurrenceTime',
    'durationMinutes',
    'icon',
  ]),
}

const DOC_KEYS = new Set([
  'title',
  'description',
  'tags',
  'completedAt',
  'status',
  'effort',
  'superUrgent',
  'dueDate',
  'dueBefore',
  'recurs',
  'recursPeriod',
  'recursDays',
  'recurrenceDay',
  'recurrenceTime',
  'durationMinutes',
  'allDay',
  'location',
  'category',
  'icon',
  'streakDays',
  'mustDoOn',
])

const DOC: Target = { keys: DOC_KEYS, nullable: new Set(['completedAt']) }

const GOAL_TREE_TASK: Target = {
  keys: new Set([
    'title',
    'effort',
    'superUrgent',
    'completedAt',
    'status',
    'dueDate',
    'recurs',
    'recursPeriod',
    'recursDays',
    'recurrenceDay',
    'recurrenceTime',
    'durationMinutes',
    'allDay',
    'mustDoOn',
  ]),
  nullable: new Set(['completedAt']),
}

const DUE_TASK: Target = {
  keys: new Set([
    'title',
    'dueDate',
    'allDay',
    'durationMinutes',
    'dueBefore',
    'completedAt',
    'location',
  ]),
  nullable: new Set(['durationMinutes', 'completedAt', 'location', 'dueDate']),
}

const REPEAT_TASK: Target = {
  keys: new Set([
    'title',
    'completedAt',
    'recursPeriod',
    'recursDays',
    'recurrenceDay',
    'recurrenceTime',
    'allDay',
    'durationMinutes',
    'location',
  ]),
  nullable: new Set([
    'completedAt',
    'recursPeriod',
    'recursDays',
    'recurrenceDay',
    'recurrenceTime',
    'durationMinutes',
    'location',
  ]),
}

const TITLE_ONLY: Target = { keys: new Set(['title']), nullable: new Set() }
const GOAL_ROW: Target = { keys: new Set(['title', 'status']), nullable: new Set() }
const FULL_TREE_TASK: Target = {
  keys: new Set(['title', 'completedAt', 'effort', 'superUrgent']),
  nullable: new Set(['completedAt']),
}
const FULL_TREE_NODE: Target = {
  keys: new Set(['title', 'completedAt']),
  nullable: new Set(['completedAt']),
}

function apply<T extends object>(row: T, input: PatchInput, target: Target): T {
  const patch = typeof input === 'function' ? input(row as NodeLike) : input
  const out = { ...row } as Record<string, unknown>
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined || (target.keys && !target.keys.has(k))) continue
    out[k] = v === null && !target.nullable.has(k) ? undefined : v
  }
  return out as T
}

const same = (a: unknown, b: unknown) => (a as string) === (b as string)

/** Apply a patch to node `id` wherever it is cached. */
export function patchNode(store: OptimisticLocalStore, id: Id<'nodes'>, patch: PatchInput) {
  for (const { args, value } of store.getAllQueries(api.dailySurface.getTodayWithContext)) {
    if (!value || !value.tasks.some((t) => same(t.taskId, id))) continue
    const tasks = value.tasks.map((t) => (same(t.taskId, id) ? apply(t, patch, TODAY) : t))
    store.setQuery(api.dailySurface.getTodayWithContext, args, {
      ...value,
      tasks,
      allCompleted: value.surfaceExists && tasks.every((t) => t.completedAt !== null),
      queued: value.queued.map((q) => {
        if (!same(q.taskId, id)) return q
        const title = (typeof patch === 'function' ? patch(q) : patch).title
        return title !== undefined ? { ...q, title } : q
      }),
    })
  }

  const habits = store.getQuery(api.nodes.getHabits, {})
  if (habits?.some((h) => same(h._id, id)))
    store.setQuery(
      api.nodes.getHabits,
      {},
      habits.map((h) => (same(h._id, id) ? apply(h, patch, HABITS) : h)),
    )

  for (const { args, value } of store.getAllQueries(api.nodes.getByParent)) {
    if (value?.some((n) => same(n._id, id)))
      store.setQuery(
        api.nodes.getByParent,
        args,
        value.map((n) => (same(n._id, id) ? apply(n, patch, DOC) : n)),
      )
  }

  const sheet = store.getQuery(api.events.taskForSheet, { taskId: id })
  if (sheet) store.setQuery(api.events.taskForSheet, { taskId: id }, apply(sheet, patch, DOC))

  for (const { args, value } of store.getAllQueries(api.nodes.getGoalTree)) {
    if (!value) continue
    const hit =
      same(value._id, id) ||
      value.milestones.some(
        (ms) =>
          same(ms._id, id) ||
          ms.targets.some((tg) => same(tg._id, id) || tg.tasks.some((t) => same(t._id, id))),
      )
    if (!hit) continue
    store.setQuery(api.nodes.getGoalTree, args, {
      ...(same(value._id, id) ? apply(value, patch, GOAL_ROW) : value),
      milestones: value.milestones.map((ms) => ({
        ...(same(ms._id, id) ? apply(ms, patch, TITLE_ONLY) : ms),
        targets: ms.targets.map((tg) => ({
          ...(same(tg._id, id) ? apply(tg, patch, TITLE_ONLY) : tg),
          tasks: tg.tasks.map((t) => (same(t._id, id) ? apply(t, patch, GOAL_TREE_TASK) : t)),
        })),
      })),
    })
  }

  const full = store.getQuery(api.nodes.getGoalsFullTree, {})
  const inFull = full?.some(
    (g) =>
      same(g._id, id) ||
      g.milestones.some(
        (ms) =>
          same(ms._id, id) ||
          ms.targets.some((tg) => same(tg._id, id) || tg.tasks.some((t) => same(t._id, id))),
      ),
  )
  if (full && inFull)
    store.setQuery(
      api.nodes.getGoalsFullTree,
      {},
      full.map((g) => ({
        ...(same(g._id, id) ? apply(g, patch, GOAL_ROW) : g),
        milestones: g.milestones.map((ms) => ({
          ...(same(ms._id, id) ? apply(ms, patch, FULL_TREE_NODE) : ms),
          targets: ms.targets.map((tg) => ({
            ...(same(tg._id, id) ? apply(tg, patch, FULL_TREE_NODE) : tg),
            tasks: tg.tasks.map((t) => (same(t._id, id) ? apply(t, patch, FULL_TREE_TASK) : t)),
          })),
        })),
      })),
    )

  const goals = store.getQuery(api.nodes.getUserGoals, {})
  if (goals?.some((g) => same(g._id, id)))
    store.setQuery(
      api.nodes.getUserGoals,
      {},
      goals.map((g) => (same(g._id, id) ? apply(g, patch, GOAL_ROW) : g)),
    )

  for (const { args, value } of store.getAllQueries(api.events.upcoming)) {
    if (!value) continue
    const hit = (xs: { _id: unknown }[]) => xs.some((t) => same(t._id, id))
    if (!hit(value.dueTasks) && !hit(value.overdue) && !hit(value.repeating)) continue
    const due = <T extends (typeof value.dueTasks)[number]>(t: T) => {
      if (!same(t._id, id)) return t
      const next = apply(t, patch, DUE_TASK)
      return { ...next, dueBefore: next.dueBefore ?? false }
    }
    const dueTasks = value.dueTasks
      .map(due)
      .filter((t) =>
        same(t._id, id) ? t.dueDate != null && t.dueDate >= args.from && t.dueDate < args.to : true,
      )
      .sort((a, b) => a.dueDate - b.dueDate)
    store.setQuery(api.events.upcoming, args, {
      ...value,
      dueTasks,
      overdue: value.overdue
        .map(due)
        .filter((t) => !same(t._id, id) || (t.dueDate != null && t.dueDate < args.from)),
      repeating: value.repeating.map((t) => (same(t._id, id) ? apply(t, patch, REPEAT_TASK) : t)),
    })
  }
}

/** Drop nodes (and, in tree reads, everything under them) from every cached read. */
export function removeNodes(store: OptimisticLocalStore, ids: readonly Id<'nodes'>[]) {
  if (!ids.length) return
  const gone = new Set(ids as readonly string[])
  const keep = (n: unknown) => !gone.has(n as string)

  for (const { args, value } of store.getAllQueries(api.dailySurface.getTodayWithContext)) {
    if (!value) continue
    const tasks = value.tasks.filter((t) => keep(t.taskId))
    const queued = value.queued.filter((q) => keep(q.taskId))
    if (tasks.length === value.tasks.length && queued.length === value.queued.length) continue
    store.setQuery(api.dailySurface.getTodayWithContext, args, {
      ...value,
      tasks,
      queued,
      allCompleted: value.surfaceExists && tasks.every((t) => t.completedAt !== null),
    })
  }

  const habits = store.getQuery(api.nodes.getHabits, {})
  if (habits?.some((h) => !keep(h._id)))
    store.setQuery(
      api.nodes.getHabits,
      {},
      habits.filter((h) => keep(h._id)),
    )

  for (const { args, value } of store.getAllQueries(api.nodes.getByParent)) {
    if (value?.some((n) => !keep(n._id)))
      store.setQuery(
        api.nodes.getByParent,
        args,
        value.filter((n) => keep(n._id)),
      )
  }

  for (const id of ids) {
    if (store.getQuery(api.events.taskForSheet, { taskId: id }))
      store.setQuery(api.events.taskForSheet, { taskId: id }, null)
  }

  for (const { args, value } of store.getAllQueries(api.nodes.getGoalTree)) {
    if (!value) continue
    if (!keep(value._id)) {
      store.setQuery(api.nodes.getGoalTree, args, null)
      continue
    }
    store.setQuery(api.nodes.getGoalTree, args, {
      ...value,
      milestones: value.milestones
        .filter((ms) => keep(ms._id))
        .map((ms) => ({
          ...ms,
          targets: ms.targets
            .filter((tg) => keep(tg._id))
            .map((tg) => ({ ...tg, tasks: tg.tasks.filter((t) => keep(t._id)) })),
        })),
    })
  }

  const full = store.getQuery(api.nodes.getGoalsFullTree, {})
  if (full)
    store.setQuery(
      api.nodes.getGoalsFullTree,
      {},
      full
        .filter((g) => keep(g._id))
        .map((g) => ({
          ...g,
          milestones: g.milestones
            .filter((ms) => keep(ms._id))
            .map((ms) => ({
              ...ms,
              targets: ms.targets
                .filter((tg) => keep(tg._id))
                .map((tg) => ({ ...tg, tasks: tg.tasks.filter((t) => keep(t._id)) })),
            })),
        })),
    )

  for (const { args, value } of store.getAllQueries(api.events.upcoming)) {
    if (
      value &&
      [value.dueTasks, value.overdue, value.repeating].some((l) => l.some((t) => !keep(t._id)))
    )
      store.setQuery(api.events.upcoming, args, {
        ...value,
        dueTasks: value.dueTasks.filter((t) => keep(t._id)),
        overdue: value.overdue.filter((t) => keep(t._id)),
        repeating: value.repeating.filter((t) => keep(t._id)),
      })
  }

  const goals = store.getQuery(api.nodes.getUserGoals, {})
  if (goals?.some((g) => !keep(g._id)))
    store.setQuery(
      api.nodes.getUserGoals,
      {},
      goals.filter((g) => keep(g._id)),
    )
}

export function removeNode(store: OptimisticLocalStore, id: Id<'nodes'>) {
  removeNodes(store, [id])
}

/**
 * Prepend a placeholder task to the cached getByParent list for `parentId`.
 * The temp id is not a real document id — never navigate to or mutate with it.
 */
export function insertTempNode(
  store: OptimisticLocalStore,
  parentId: Id<'nodes'>,
  node: Partial<Doc<'nodes'>> & { title: string },
): Id<'nodes'> | null {
  const list = store.getQuery(api.nodes.getByParent, { parentId })
  if (!list) return null
  const id = `optimistic-${crypto.randomUUID()}` as Id<'nodes'>
  const row: Doc<'nodes'> = {
    _id: id,
    _creationTime: Date.now(),
    userId: list[0]?.userId ?? '',
    parentId,
    type: 'task',
    category: list[0]?.category ?? '',
    completedAt: null,
    aiGenerated: false,
    progress: 0,
    effort: 'medium',
    skippedCount: 0,
    streakDays: 0,
    ...node,
  }
  store.setQuery(api.nodes.getByParent, { parentId }, [row, ...list])
  return id
}

/** nodes.complete: stamp completedAt and fill today's habit dot. */
export function completeNode(store: OptimisticLocalStore, id: Id<'nodes'>) {
  const now = Date.now()
  patchNode(store, id, (prev) => ({
    completedAt: now,
    ...(prev.streakDays != null && { streakDays: prev.streakDays + 1 }),
  }))
  optimisticHabitCompletion(store, id as string, true)
}

/** nodes.uncomplete: clear completedAt and send the status back to backlog. */
export function uncompleteNode(store: OptimisticLocalStore, id: Id<'nodes'>) {
  patchNode(store, id, (prev) =>
    prev.completedAt == null
      ? {}
      : {
          completedAt: null,
          status: 'backlog',
          ...(prev.streakDays != null && { streakDays: Math.max(0, prev.streakDays - 1) }),
        },
  )
  optimisticHabitCompletion(store, id as string, false)
}

// Mirrors of the server patches for common node mutations; each takes the mutation's args.

export function optimisticRename(
  store: OptimisticLocalStore,
  a: { nodeId: Id<'nodes'>; title: string; description?: string | null; tags?: string[] },
) {
  const title = a.title.trim()
  if (!title) return
  patchNode(store, a.nodeId, {
    title,
    ...(a.description !== undefined && { description: a.description }),
    ...(a.tags !== undefined && { tags: a.tags }),
  })
}

export function optimisticDueDate(
  store: OptimisticLocalStore,
  a: { taskId: Id<'nodes'>; dueDate?: number },
) {
  patchNode(store, a.taskId, { dueDate: a.dueDate ?? null, dueBefore: null })
}

export function optimisticRecurs(
  store: OptimisticLocalStore,
  a: {
    taskId: Id<'nodes'>
    recursPeriod?: RecursPeriod
    recursDays?: number[]
    recurrenceTime?: string
    recurrenceDay?: number
  },
) {
  patchNode(store, a.taskId, (prev) => {
    const s = a.recursPeriod
      ? resolveSchedule(a.recursPeriod, a.recursDays ?? prev.recursDays)
      : null
    return {
      recursPeriod: s?.recursPeriod ?? null,
      recurs: s ? true : null,
      recursDays: s?.recursDays ?? null,
      recurrenceDay: s?.recurrenceDay ?? a.recurrenceDay,
      recurrenceTime:
        a.recurrenceTime !== undefined
          ? a.recurrenceTime || null
          : s && !prev.recurrenceTime
            ? '09:00'
            : undefined,
    }
  })
}

export function optimisticAllDay(
  store: OptimisticLocalStore,
  a: { taskId: Id<'nodes'>; allDay: boolean },
) {
  patchNode(store, a.taskId, (prev) =>
    a.allDay
      ? { allDay: true, recurrenceTime: null, durationMinutes: null }
      : {
          allDay: false,
          ...(prev.recursPeriod && { recurrenceTime: prev.recurrenceTime ?? '09:00' }),
        },
  )
}

export function optimisticDuration(
  store: OptimisticLocalStore,
  a: { taskId: Id<'nodes'>; durationMinutes: number },
) {
  patchNode(store, a.taskId, {
    durationMinutes: Math.max(5, Math.round(a.durationMinutes / 5) * 5),
  })
}

/** nodes.setTaskStatus: 'done' completes, leaving done un-completes, otherwise just the status. */
export function optimisticTaskStatus(
  store: OptimisticLocalStore,
  a: { taskId: Id<'nodes'>; status: 'backlog' | 'in-progress' | 'done' },
) {
  const now = Date.now()
  patchNode(store, a.taskId, (prev) => {
    const streak = prev.streakDays
    if (a.status === 'done')
      return prev.completedAt != null
        ? {}
        : { status: 'done', completedAt: now, ...(streak != null && { streakDays: streak + 1 }) }
    if (prev.completedAt != null)
      return {
        status: a.status,
        completedAt: null,
        ...(streak != null && { streakDays: Math.max(0, streak - 1) }),
      }
    return { status: a.status }
  })
  optimisticHabitCompletion(store, a.taskId as string, a.status === 'done')
}

/** nodes.deleteCompletedInboxTasks: drop the inbox's completed, non-recurring tasks. */
export function optimisticDeleteCompletedInbox(
  store: OptimisticLocalStore,
  inboxId?: Id<'nodes'> | null,
) {
  const parentId =
    inboxId ?? store.getQuery(api.nodes.getUserGoals, {})?.find((g) => g.isInbox)?._id
  if (!parentId) return
  const list = store.getQuery(api.nodes.getByParent, { parentId })
  if (!list) return
  removeNodes(
    store,
    list.filter((n) => n.completedAt !== null && n.recurs !== true).map((n) => n._id),
  )
}

/**
 * nodes.createQuickTask: a placeholder row at the top of the cached inbox list,
 * mirroring the server's defaults. Number habits (server-derived category) are left to the server.
 */
export function optimisticQuickTask(
  store: OptimisticLocalStore,
  a: {
    title: string
    description?: string
    tags?: string[]
    recursPeriod?: RecursPeriod
    recursDays?: number[]
    recurrenceTime?: string
    recurrenceTzOffset?: number
    recurrenceDay?: number
    dueDate?: number
    durationMinutes?: number
    allDay?: boolean
    location?: string
    dueBefore?: boolean
    effort?: 'low' | 'medium' | 'high'
    isHabit?: boolean
    category?: string
    mustDoOn?: string
    measure?: unknown
    icon?: string
  },
) {
  if (a.measure) return
  const inboxId = store.getQuery(api.nodes.getUserGoals, {})?.find((g) => g.isInbox)?._id
  if (!inboxId) return
  const place = cleanLocation(a.location)
  const recurring = !!(a.recursPeriod || a.isHabit)
  insertTempNode(store, inboxId, {
    title: a.title.trim(),
    ...(a.description && { description: a.description }),
    ...(a.tags?.length && { tags: a.tags }),
    ...(a.mustDoOn && !a.isHabit && { mustDoOn: a.mustDoOn, mustDoSince: a.mustDoOn }),
    category: a.category ?? 'inbox',
    effort: a.effort ?? 'medium',
    ...((a.allDay ?? (!a.recurrenceTime && a.durationMinutes === undefined)) && { allDay: true }),
    ...(!a.isHabit && place && { location: place }),
    ...(!a.isHabit && a.dueDate && a.dueBefore && { dueBefore: true }),
    ...(a.isHabit && { isHabit: true }),
    ...(a.isHabit && a.icon && HABIT_ICONS.includes(a.icon) && { icon: a.icon }),
    ...(recurring && {
      recurs: true,
      recurrenceTime: a.recurrenceTime ?? '09:00',
      ...(a.recurrenceTzOffset !== undefined && { recurrenceTzOffset: a.recurrenceTzOffset }),
      ...(a.recurrenceDay !== undefined && { recurrenceDay: a.recurrenceDay }),
      ...resolveSchedule(a.recursPeriod, a.recursDays),
    }),
    ...(a.dueDate &&
      !a.recursPeriod && {
        dueDate: a.dueDate,
        ...(a.durationMinutes !== undefined && {
          durationMinutes: Math.max(15, Math.round(a.durationMinutes / 15) * 15),
        }),
      }),
  })
}
